import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import JitsiMeeting from './JitsiMeeting';
import TeacherJoinModal from './TeacherJoinModal';
import { auth as authAPI } from '../../services/api';
import api from '../../services/api';
import './VideoMeetingPage.css';

/**
 * GuestMeetingPage Component
 *
 * Allows teachers (instructors) to join student meetings via direct link
 * Teacher Registration Flow:
 * 1. Shows TeacherJoinModal for teacher to sign in with Google
 * 2. Creates teacher account in backend with role='teacher' and 7-day trial
 * 3. Verifies room owner (student) has active subscription
 * 4. Loads Jitsi meeting with teacher's JWT token
 * Teachers are redirected to /home after leaving (their dashboard)
 *
 * Note: Teachers get persistent accounts, not guest accounts
 */
const GuestMeetingPage = () => {
  const { roomId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(true);
  const [teacherLoggedIn, setTeacherLoggedIn] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [jitsiToken, setJitsiToken] = useState('');
  const [teacherName, setTeacherName] = useState('');
  const [error, setError] = useState('');

  // Check if teacher already logged in (has token in localStorage)
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const savedTeacherName = localStorage.getItem('userName');

    if (authToken && savedTeacherName) {
      setTeacherLoggedIn(true);
      setTeacherName(savedTeacherName);
      setShowJoinModal(false);
      // Verify room access immediately
      verifyRoomAccess();
    }
  }, []);

  const handleTeacherJoin = async (signupData) => {
    setError('');
    setLoading(true);

    try {
      // Register/login teacher with Google OAuth
      const response = await authAPI.webSignupOrSignin(signupData);
      const { token, user, requiresRole, isNewUser } = response.data;

      // Check if we need to show role selection (should not happen - we always send role='teacher')
      if (requiresRole) {
        setError('Please try again. Role selection required.');
        setLoading(false);
        return;
      }

      // Store auth token and user info
      localStorage.setItem('authToken', token);
      localStorage.setItem('userName', user.name);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('userRole', user.userRole);

      setTeacherLoggedIn(true);
      setTeacherName(user.name);
      setShowJoinModal(false);

      console.log(isNewUser ? '✅ New teacher account created' : '✅ Teacher signed in');

      // Verify room access
      await verifyRoomAccess();
    } catch (err) {
      console.error('Failed to sign in as teacher:', err);
      setError(err.response?.data?.error || 'Failed to join meeting. Please try again.');
      setLoading(false);
      throw err; // Re-throw so modal can handle it
    }
  };

  const verifyRoomAccess = async () => {
    if (!roomId) {
      setError('Invalid meeting link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Call backend to verify if room owner has active subscription
      const response = await api.get(`/meetings/verify/${roomId}`);
      const { hasAccess: access, message, token } = response.data;

      setHasAccess(access);
      if (access && token) {
        setJitsiToken(token);
      } else if (!access) {
        setError(message || 'This meeting room is no longer available');
      }
    } catch (err) {
      console.error('Failed to verify room access:', err);
      setError('Unable to verify meeting access. The host may not have an active subscription.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleLeaveMeeting = () => {
    // Redirect teacher to their homepage (they have a persistent account)
    window.location.href = '/home';
  };

  // Show join modal if teacher hasn't logged in yet
  if (showJoinModal && !teacherLoggedIn) {
    return (
      <>
        <TeacherJoinModal
          onJoin={handleTeacherJoin}
          onError={handleModalError}
        />
        {error && (
          <div className="video-meeting-page">
            <div className="meeting-content">
              <div className="error-card" style={{ maxWidth: '500px', margin: '50px auto' }}>
                {error}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading spinner while verifying access
  if (loading) {
    return (
      <div className="video-meeting-page">
        <div className="meeting-content" style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner"></div>
          <p>Verifying meeting access...</p>
        </div>
      </div>
    );
  }

  // Show error if no access or error occurred
  if (error || !hasAccess) {
    return (
      <div className="video-meeting-page">
        <div className="meeting-content">
          <div className="meeting-create">
            <div className="icon-container">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#f44336" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h2>Meeting Not Available</h2>
            <p className="meeting-description">
              {error || 'This meeting room is no longer accessible.'}
            </p>
            <p className="meeting-description" style={{ marginTop: '20px', fontSize: '14px', opacity: 0.8 }}>
              The host may not have an active subscription or the meeting link may be invalid.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show Jitsi meeting
  return (
    <div className="video-meeting-page">
      <JitsiMeeting
        meetingId={roomId}
        roomName={roomId}
        jwtToken={jitsiToken}
        displayName={teacherName}
        onLeave={handleLeaveMeeting}
      />
    </div>
  );
};

export default GuestMeetingPage;
